import { Grid, useMediaQuery } from '@mui/material'
import React, { useMemo, useCallback, useState } from 'react'

// Common Components
import TableTemplate, { Column } from '../../Common/Table/TableTemplate'

// Redux
import { useAppSelector } from '../../../redux/store'

// Task Components
import TaskBadge from './TaskTableComponents/TaskBadge'
import TaskDueDateFormat from './TaskTableComponents/TaskDueDateFormat'
import TaskViewModal from './TaskActions/TaskViewModal'
import TaskAttachImageModal from './TaskActions/TaskAttachImageModal'
import TaskTitle from './TaskTableComponents/TaskTitle'
import { Task } from '../../../redux/task/types'
import { selectLoadingFetchAssignedTask } from '../../../redux/task/selector'
import { useUpdateAssignedTask } from '../../../hooks/taskHooks/useUpdateAssignedTask'
import useConfirmationAlert from '../../../hooks/componentHooks/useConfirmationAlert'
import showToast from '../../Common/SweetAlert/ToastAlert'
import { selectUserTeamMemberInfo } from '../../../redux/auth/selector'

interface ViewTaskTableProps {
  data: Task[]
  refreshTable: boolean
}

const ViewTaskTable: React.FC<ViewTaskTableProps> = ({ data, refreshTable }) => {
  const loading = useAppSelector(selectLoadingFetchAssignedTask)
  const error = useAppSelector((state) => state.task.errorAssignTask)
  const state = { loading, error }

  const { updateTask } = useUpdateAssignedTask()
  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  // State for managing image modal
  const [showImageModal, setShowImageModal] = useState<boolean>(false)
  const [pendingTask, setPendingTask] = useState<Task | null>(null)
  const [pendingStatus, setPendingStatus] = useState<string>('')

  const isSmall = useMediaQuery('(max-width:1300px)')

  // Custom sorting function for multi-level sorting
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      // Define status priority (descending order)
      const statusPriority: Record<string, number> = {
        notStarted: 3,
        inProgress: 2,
        completed: 1,
      }

      // Define importance priority (for tertiary sort - Required first)
      const importancePriority: Record<string, number> = {
        Required: 3,
        Optional: 1,
      }

      // Primary sort: Status (descending)
      const statusA = statusPriority[a.status || 'notStarted'] || 0
      const statusB = statusPriority[b.status || 'notStarted'] || 0
      if (statusA !== statusB) {
        return statusB - statusA // Descending order
      }

      // Secondary sort: Due Date (ascending)
      const dueDateA = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER
      const dueDateB = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER
      if (dueDateA !== dueDateB) {
        return dueDateA - dueDateB // Ascending order
      }

      // Tertiary sort: Importance (Required first, then Optional)
      const importanceA = importancePriority[a.importance || 'Optional'] || 0
      const importanceB = importancePriority[b.importance || 'Optional'] || 0
      if (importanceA !== importanceB) {
        return importanceB - importanceA // Descending order (Required > Optional)
      }

      // Quaternary sort: Completion Date (descending - most recent first)
      const completionDateA = a.completionDate ? new Date(a.completionDate).getTime() : 0
      const completionDateB = b.completionDate ? new Date(b.completionDate).getTime() : 0
      return completionDateB - completionDateA // Descending order (most recent completion first)
    })
  }, [data])

  const handleStatusChange = useCallback(
    async (newStatus: string, task: Task) => {
      const statusMapping: Record<string, string> = {
        notStarted: 'Not Started',
        inProgress: 'In Progress',
        completed: 'Completed',
      }

      // Check if trying to complete a task that requires documentation but has no image
      if (
        newStatus === 'completed' &&
        task.required === true &&
        (!task.imageFilename || task.imageFilename === 'DefaultTaskImage')
      ) {
        openConfirmationAlert({
          title: 'Documentation Required',
          text: 'This task requires documentation to be completed. Would you like to upload an image now?',
          onConfirm: () => {
            setPendingTask(task)
            setPendingStatus(newStatus)
            setShowImageModal(true)
          },
        })
        return
      }

      openConfirmationAlert({
        title: 'Update Task Status',
        text: `Are you sure you want to change the task status to "${statusMapping[newStatus] || newStatus}"?`,
        onConfirm: async () => {
          try {
            const updatePayload = {
              taskId: task.id!,
              teamMemberEmail: teamMemberDetails.email,
              teamMemberWorkdayId: teamMemberDetails.workdayId,
              status: newStatus,
              // Clear started date if status is set to "Not Started"
              ...(newStatus === 'notStarted' && { startedDate: '' }),
            }

            await updateTask(updatePayload)
          } catch (error) {
            console.error('Error updating task status:', error)
            showToast('error', 'Failed to update task status')
          }
        },
      })
    },
    [
      openConfirmationAlert,
      teamMemberDetails.email,
      teamMemberDetails.workdayId,
      updateTask,
      setPendingTask,
      setPendingStatus,
      setShowImageModal,
    ],
  )

  const handleImageAttached = useCallback(async () => {
    // After image is attached, proceed with the pending status change
    if (pendingTask && pendingStatus === 'completed') {
      try {
        const updatePayload = {
          taskId: pendingTask.id!,
          teamMemberEmail: teamMemberDetails.email,
          teamMemberWorkdayId: teamMemberDetails.workdayId,
          status: 'completed',
        }

        await updateTask(updatePayload)
        showToast('success', 'Task completed successfully!')
      } catch (error) {
        console.error('Error updating task status:', error)
        showToast('error', 'Failed to update task status')
      }
    }

    // Reset state
    setPendingTask(null)
    setPendingStatus('')
    setShowImageModal(false)
  }, [pendingTask, pendingStatus, teamMemberDetails.email, teamMemberDetails.workdayId, updateTask])

  const columns = useMemo<Column[]>(
    () => [
      {
        field: 'title',
        headerName: 'TITLE',
        headerAlign: 'center',
        bodyAlignment: 'left',
        sortable: true,
        width: '200px',
        renderCell: (_params: any, row: Task) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: isSmall ? '0px' : '40px',
              paddingRight: isSmall ? '0px' : '8px',
              width: '100%',
            }}
            title={row.title}
          >
            <TaskTitle value={row.title} />
          </div>
        ),
      },
      {
        field: 'details',
        headerName: 'DESCRIPTION',
        headerAlign: 'center',
        bodyAlignment: 'left',
        sortable: false,
        width: '250px',
        renderCell: (_params: any, row: Task) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: isSmall ? '0px' : '16px',
              paddingRight: isSmall ? '0px' : '16px',
              width: '100%',
            }}
            title={row.details || 'No description'}
          >
            <span
              style={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                fontSize: '12px',
                color: row.details ? 'inherit' : '#999',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxHeight: '58.8px', // 3 lines * 19.6px line height (14px * 1.4)
              }}
            >
              {row.details || 'No description'}
            </span>
          </div>
        ),
      },
      {
        field: 'importance',
        headerName: 'IMPORTANCE',
        headerAlign: 'center',
        bodyAlignment: 'center',
        sortable: false,
        width: '120px',
        renderCell: (_params: any, row: Task) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: isSmall ? '0px' : '16px',
              paddingRight: isSmall ? '0px' : '16px',
              width: '100%',
            }}
          >
            <TaskBadge value={row.importance} badgeType='importanceBadge' />
          </div>
        ),
      },
      {
        field: 'status',
        headerName: 'STATUS',
        headerAlign: 'center',
        bodyAlignment: 'center',
        width: '120px',
        renderCell: (_params: any, row: Task) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingLeft: isSmall ? '0px' : '16px',
                paddingRight: isSmall ? '0px' : '16px',
                width: '100%',
              }}
              data-testid={`task-status-cell-${row.id}`}
            >
              <TaskBadge
                value={row.status || 'notStarted'}
                badgeType='statusBadge'
                onChange={(newStatus) => handleStatusChange(newStatus, row)}
              />
            </div>
          )
        },
      },
      {
        field: 'dueDate',
        headerName: 'DUE DATE',
        headerAlign: 'center',
        bodyAlignment: 'center',
        sortable: true,
        width: '120px',
        renderCell: (_params: any, row: Task) => (
          <div
            data-testid={`task-due-date-cell-${row.id}`}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingLeft: isSmall ? '0px' : '16px',
              paddingRight: isSmall ? '0px' : '16px',
              width: '100%',
            }}
          >
            <TaskDueDateFormat value={row.dueDate?.toString() || ''} />
          </div>
        ),
      },
      {
        field: 'completionDate',
        headerName: 'COMPLETION DATE',
        headerAlign: 'center',
        bodyAlignment: 'center',
        sortable: true,
        width: '150px',
        renderCell: (_params: any, row: Task) => (
          <div
            data-testid={`task-completion-date-cell-${row.id}`}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingLeft: isSmall ? '0px' : '16px',
              paddingRight: isSmall ? '0px' : '16px',
              width: '100%',
            }}
          >
            {row.completionDate ? <TaskDueDateFormat value={row.completionDate.toString()} /> : '-'}
          </div>
        ),
      },
      {
        field: 'action',
        headerName: 'ACTION',
        headerAlign: 'center',
        bodyAlignment: 'center',
        width: '120px',
        renderCell: (_params: any, row: Task) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              paddingLeft: isSmall ? '0px' : '8px',
              paddingRight: isSmall ? '0px' : '8px',
              width: '100%',
            }}
          >
            <TaskViewModal task={row} />
            <TaskAttachImageModal task={row} />
          </div>
        ),
      },
    ],
    [refreshTable, handleStatusChange],
  )

  return (
    <Grid data-testid='view-task-table'>
      <TableTemplate
        columns={columns}
        datas={sortedData}
        state={state}
        refreshTable={refreshTable}
        uniqueField='id'
      />
      {ConfirmationAlert}
      {showImageModal && pendingTask && (
        <TaskAttachImageModal
          task={pendingTask}
          onImageAttached={handleImageAttached}
          open={showImageModal}
          onClose={() => {
            setPendingTask(null)
            setPendingStatus('')
            setShowImageModal(false)
          }}
        />
      )}
    </Grid>
  )
}

export default ViewTaskTable
